import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

function QuizQuestions() {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [filters, setFilters] = useState({
        quizPage: '',
        isActive: ''
    });

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        fetchQuestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchQuestions = async (page = 1) => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page,
                limit: 50,
                ...(filters.quizPage && { quizPage: filters.quizPage }),
                ...(filters.isActive && { isActive: filters.isActive }),
            });

            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/quiz/questions/list?${queryParams}`
            );
            const data = await res.json();

            if (data.status === 'success') {
                setQuestions(data.data.questions || []);
                setPagination(data.data.pagination || {});
            } else {
                toast.error(data.message || 'Failed to fetch quiz questions');
            }
        } catch (error) {
            console.error('Error fetching quiz questions:', error);
            toast.error('Error fetching quiz questions');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (question) => {
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/quiz/questions/${question._id}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ isActive: !question.isActive })
                }
            );

            const data = await res.json();
            if (data.status === 'success') {
                toast.success(`Question ${!question.isActive ? 'activated' : 'deactivated'}`);
                fetchQuestions(pagination.page || 1);
            } else {
                toast.error(data.message || 'Update failed');
            }
        } catch (error) {
            console.error('Error toggling question:', error);
            toast.error('Error toggling question');
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/quiz/questions/${deleteId}`,
                { method: 'DELETE' }
            );

            const data = await res.json();
            if (data.status === 'success') {
                toast.success('Quiz question deleted successfully');
                setDeleteId(null);
                window.$?.('#modaldelete_question').modal('hide');
                fetchQuestions(pagination.page || 1);
            } else {
                toast.error(data.message || 'Delete failed');
            }
        } catch (error) {
            console.error('Error deleting question:', error);
            toast.error('Error deleting question');
        } finally {
            setDeleting(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchQuestions(1);
    };

    const handleClearFilters = () => {
        setFilters({ quizPage: '', isActive: '' });
        setTimeout(() => fetchQuestions(1), 0);
    };

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Quiz Questions</h1>
                        </div>
                        <div className="col-sm-6 txt_rgt">
                            <Link to="/quizQuestionsAdd" className="btn btn-blue">
                                Add Question
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    {/* Filters */}
                    <div className="card mb-3">
                        <div className="card-body">
                            <form onSubmit={handleSearch} className="row align-items-end">
                                <div className="col-md-3">
                                    <label>Quiz Page</label>
                                    <select
                                        className="form-control"
                                        name="quizPage"
                                        value={filters.quizPage}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All</option>
                                        <option value="1">Page 1</option>
                                        <option value="2">Page 2</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label>Active</label>
                                    <select
                                        className="form-control"
                                        name="isActive"
                                        value={filters.isActive}
                                        onChange={handleFilterChange}
                                    >
                                        <option value="">All</option>
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <button type="submit" className="btn btn-primary mr-2">
                                        Filter
                                    </button>
                                    <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
                                        Clear
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center p-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                ) : questions.length > 0 ? (
                                    <>
                                        <table width="100%" className="table table-striped table-bordered txt_lft">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '50px' }}>Order</th>
                                                    <th style={{ width: '80px' }}>Page</th>
                                                    <th>Question Key</th>
                                                    <th>Question Text</th>
                                                    <th style={{ width: '130px' }}>Type</th>
                                                    <th style={{ width: '80px' }}>Options</th>
                                                    <th style={{ width: '100px' }}>Active</th>
                                                    <th style={{ width: '100px' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {questions.map((question) => (
                                                    <tr key={question._id}>
                                                        <td>{question.displayOrder}</td>
                                                        <td>{question.page}</td>
                                                        <td>
                                                            <code>{question.questionKey}</code>
                                                        </td>
                                                        <td>{question.questionText}</td>
                                                        <td>{question.questionType}</td>
                                                        <td>{(question.options || []).length}</td>
                                                        <td align="center">
                                                            <div className="toggle-switch">
                                                                <label className="switch">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={question.isActive}
                                                                        onChange={() => handleToggleActive(question)}
                                                                    />
                                                                    <span className="slider"></span>
                                                                </label>
                                                                <span className="toggle-label">
                                                                    {question.isActive ? 'On' : 'Off'}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div align="center">
                                                                <Link to={`/quizQuestionsAdd/${question._id}`} className="crsr_pntr">
                                                                    <i className="fa-solid fa-pen mr-2" title="Edit" />
                                                                </Link>
                                                                <button
                                                                    className="crsr_pntr btn btn-link p-0"
                                                                    onClick={() => setDeleteId(question._id)}
                                                                    data-toggle="modal"
                                                                    data-target="#modaldelete_question"
                                                                >
                                                                    <i className="fa-solid fa-trash-can" title="Delete" />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* Pagination */}
                                        {pagination.pages > 1 && (
                                            <nav className="mt-3">
                                                <ul className="pagination justify-content-center">
                                                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => fetchQuestions(pagination.page - 1)}
                                                            disabled={pagination.page === 1}
                                                        >
                                                            Previous
                                                        </button>
                                                    </li>
                                                    {[...Array(pagination.pages)].map((_, i) => (
                                                        <li key={i} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => fetchQuestions(i + 1)}
                                                            >
                                                                {i + 1}
                                                            </button>
                                                        </li>
                                                    ))}
                                                    <li className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => fetchQuestions(pagination.page + 1)}
                                                            disabled={pagination.page === pagination.pages}
                                                        >
                                                            Next
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-center p-5">
                                        <p>No quiz questions found</p>
                                        <Link to="/quizQuestionsAdd" className="btn btn-primary">
                                            Create your first question
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Modal */}
            <div className="modal fade" id="modaldelete_question" tabIndex="-1">
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Confirm Delete</h5>
                            <button type="button" className="close" data-dismiss="modal">
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete this quiz question?</p>
                            <p className="text-muted small">
                                This removes it from the live quiz immediately. Leads who already answered it keep
                                their recorded answer.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-dismiss="modal">
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleDelete}
                                disabled={deleting}
                            >
                                {deleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default QuizQuestions
